import React, { useEffect, useState } from 'react'
import { EcomDataLayerHub } from '../../javascript/analytics/ecommerceDataLayerEventHub.services'
import { IAddOnProgram, IEpiProps, IEventRegistrationRefreshModel, ISpecialtyProgram, IUserProgramSelection, IUserSelections, StepConstants } from '../models/event-reg-refresh-models'
import { useEventRegRefreshContext, useEventRegRefreshDispatch } from '../reducers/event-reg-refresh-reducers'
import { ContentWrapper } from './layout/content-wrapper'
import { MainContent } from './layout/main-content'
import { SideBarContent } from './layout/sidebar-content'
import { ProductBasedAddonCard } from './product-based-addon-card'
import { ProgramCard } from './program-card'
import { ProgramSummary } from './program-summary'


interface IContextProps {
  epiProps: IEpiProps
  currentStep: string
  eventRegistrationRefreshModel: IEventRegistrationRefreshModel
  userSelections: IUserSelections
}

export const AddOnsStep = () => {
  const { currentStep, eventRegistrationRefreshModel, userSelections, epiProps }: IContextProps = useEventRegRefreshContext()!
  const dispatch = useEventRegRefreshDispatch()!
  const [addOnPrograms, setAddOnPrograms] = useState<IAddOnProgram[]>([])
  const [selectedSpecialtyProgram, setSelectedSpecialtyProgram] = useState<ISpecialtyProgram | null>(null)

  // Effect to load add-ons based on selected specialty program
  useEffect(() => {
    if (currentStep == StepConstants.AddOnsStep) {
      EcomDataLayerHub.viewAddons(userSelections.userProgramSelection.programName, userSelections.userAddOnProgramSelections?.[0] ?? null)
    }
}, [currentStep])

  useEffect(() => {
    if (userSelections && userSelections.userProgramSelection && userSelections.userProgramSelection.programCode) {
      // Find the selected specialty program
      const specialty = getSelectedSpecialtyProgram(userSelections.userProgramSelection.programCode, userSelections.userProgramSelection.isRenewal, userSelections.userProgramSelection.isDirectDelivery)
      setSelectedSpecialtyProgram(specialty)

      // Load add-on programs
      if (specialty) {
        setAddOnPrograms(specialty.addOnPrograms || [])
      }
    }
  }, [userSelections.userProgramSelection, eventRegistrationRefreshModel])

  // Function to get the selected specialty program
  const getSelectedSpecialtyProgram = (specialtyCode: string, isRenewal: boolean, isDirectDelivery: boolean): ISpecialtyProgram | null => {
    if (!eventRegistrationRefreshModel || !eventRegistrationRefreshModel.specialtyPrograms) {
      return null
    }

    return eventRegistrationRefreshModel.specialtyPrograms.find(
      (program: ISpecialtyProgram) => program.programCode === specialtyCode && program.isRenewal == isRenewal && program.isDirectDelivery == isDirectDelivery
    ) || null
  }

  const isAddonSelected = (program: IAddOnProgram): boolean => {
    return (userSelections.userAddOnProgramSelections || []).some(sel =>
      program.programSku && sel.programSku
        ? sel.programSku === program.programSku
        : sel.programCode === program.programCode
    )
  }

  // Handle add-on program selection - toggle in/out of the selections array
  const handleAddOnProgramSelect = (program: IAddOnProgram) => {
    const isRenewal = selectedSpecialtyProgram ? selectedSpecialtyProgram.isRenewal : false
    const isDirectDelivery = selectedSpecialtyProgram ? selectedSpecialtyProgram.isDirectDelivery : false
    const price = program.programPrice

    const currentSelections: IUserProgramSelection[] = userSelections.userAddOnProgramSelections || []
    const alreadySelected = isAddonSelected(program)

    let updatedSelections: IUserProgramSelection[]
    if (alreadySelected) {
      // Deselect: remove from array
      updatedSelections = currentSelections.filter(sel =>
        program.programSku && sel.programSku
          ? sel.programSku !== program.programSku
          : sel.programCode !== program.programCode
      )
    } else {
      const baseSelections = currentSelections.filter(sel => sel.disableDateTimeSelection || !!sel.programSku)
      updatedSelections = [...baseSelections, {
        programName: program.programName,
        programCode: program.programCode,
        isRenewal,
        isDirectDelivery,
        disableDateTimeSelection: program.disableDateTimeSelection,
        programSku: program.programSku,
      }]
      EcomDataLayerHub.selectAddon(program.programName, price)
    }

    dispatch({
      type: 'updateUserSelections',
      data: {
        ...userSelections,
        userAddOnProgramSelections: updatedSelections,
        userProgramDateTimeSelection: null,
        userAddOnProgramDateTimeSelection: null,
        attendees: []
      }
    })
    dispatch({ type: 'clearSharedProgramDateTimeEvents' })
    dispatch({ type: 'resetStepsAfter', data: StepConstants.AddOnsStep })
  }

  const handleContinue = () => {
    const nextStep = StepConstants.DateTimeSpecialtyProgramStep
    EcomDataLayerHub.continueToDateTime(StepConstants.AddOnsStep, nextStep)
    dispatch({ type: 'updateCurrentStep', data: nextStep })
  }

  if (currentStep == StepConstants.AddOnsStep) {
    return (
      <>
        <section className="app-section-header">
          <div className="app-section-header-inner">
            <h1>{epiProps.addOnProgramStepTitle}</h1>
            <p>{epiProps.addOnProgramStepDescription}</p>
          </div>
        </section>
        <ContentWrapper>
          <MainContent>
            <div>
              {addOnPrograms.length > 0 && addOnPrograms.map((program, index) =>
                program.disableDateTimeSelection ? (
                  <ProductBasedAddonCard
                    key={`${program.programSku ?? program.programCode}-${index}`}
                    program={program}
                    isSelected={isAddonSelected(program)}
                    onSelect={() => handleAddOnProgramSelect(program)}
                    showPrice={eventRegistrationRefreshModel.showPrice}
                    currency={eventRegistrationRefreshModel.currency}
                  />
                ) : (
                  <ProgramCard
                    key={`${program.programCode}-${index}`}
                    program={program}
                    isSelected={isAddonSelected(program)}
                    programType={'addon'}
                    onSelect={() => handleAddOnProgramSelect(program)}
                    showPrice={eventRegistrationRefreshModel.showPrice}
                    currency={eventRegistrationRefreshModel.currency}
                  />
                )
              )}
            </div>
          </MainContent>
          <SideBarContent>
            <ProgramSummary
              userSelections={userSelections}
              onContinue={handleContinue}
              showValidationMessage={false}
              validationMessage=""
            />
          </SideBarContent>
        </ContentWrapper>
      </>
    )
  }

  return null
}